import { useCallback, useMemo } from "react";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PathPickOrderStatusGroup } from "@/features/pathpick/features/order-history/types";
import { ORDER_STATUS_FILTERS } from "@/features/pathpick/features/order-history/constants";

export interface PathPickOrderHistoryFilter {
  // 상태 탭 — 디폴트("ALL")는 쿼리스트링에서 생략한다.
  statusGroup?: PathPickOrderStatusGroup;
  // 적용된 검색어([검색]/Enter로 확정된 값). 입력 중인 초안은 화면 로컬 상태다.
  query?: string;
  // 주문일 범위 — 사용자가 고른 날짜를 그대로 저장하고, 종료일 보정은 요청 조립 시점에만 한다.
  startDate?: Date;
  endDate?: Date;
  pageNumber?: number;
  pageSize?: number;
}

export type PathPickOrderHistoryFilterKey = keyof PathPickOrderHistoryFilter;

const FILTER_KEYS: PathPickOrderHistoryFilterKey[] = [
  "statusGroup",
  "query",
  "startDate",
  "endDate",
  "pageNumber",
  "pageSize",
];

const STATUS_GROUPS = ORDER_STATUS_FILTERS.map((filter) => filter.value);
const DEFAULT_STATUS_GROUP: PathPickOrderStatusGroup = "ALL";
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_PAGE_SIZE = 20;

// 관리하는 모든 필터 키를 항상 포함(빈 값은 undefined)시켜 쿼리스트링 관리 키를 일관되게 유지한다.
function normalize(
  filter: PathPickOrderHistoryFilter
): PathPickOrderHistoryFilter {
  return FILTER_KEYS.reduce((acc, key) => {
    const value = filter[key];
    // Date는 length가 없어 아래 문자열 분기에 걸리면 조용히 유실된다.
    if (value instanceof Date) {
      acc[key] = value as any;
    } else if (typeof value === "number") {
      acc[key] = value as any;
    } else {
      acc[key] = value && value.length > 0 ? (value as any) : undefined;
    }
    return acc;
  }, {} as PathPickOrderHistoryFilter);
}

// 주문내역의 탭/검색어/주문일/페이지네이션 상태 — 새로고침·URL 공유에도 유지되도록 쿼리스트링으로 관리한다.
const usePathPickOrderHistoryFilter = () => {
  const [rawFilter, setFilter] =
    useTypedSearchParams<PathPickOrderHistoryFilter>();

  // useTypedSearchParams는 필드명이 아니라 "값 모양"으로 타입을 고른다.
  // 검색어가 "2026-07-21"이나 "true"처럼 생기면 Date/boolean으로 복원되므로
  // (공용 훅은 건드리지 않고) 여기서 기대 타입만 채택해 선언 타입 불변식을 복원한다.
  const filter = useMemo<PathPickOrderHistoryFilter>(
    () => ({
      ...rawFilter,
      query: typeof rawFilter.query === "string" ? rawFilter.query : undefined,
      startDate:
        rawFilter.startDate instanceof Date ? rawFilter.startDate : undefined,
      endDate:
        rawFilter.endDate instanceof Date ? rawFilter.endDate : undefined,
    }),
    [rawFilter]
  );

  // URL에 없는 상태 그룹이 들어와도(수동 편집 등) 디폴트 탭으로 떨어뜨린다.
  const statusGroup =
    filter.statusGroup && STATUS_GROUPS.includes(filter.statusGroup)
      ? filter.statusGroup
      : DEFAULT_STATUS_GROUP;
  const pageNumber = filter.pageNumber ?? DEFAULT_PAGE_NUMBER;
  const pageSize = filter.pageSize ?? DEFAULT_PAGE_SIZE;

  // 기본값은 URL에서 생략해 쿼리스트링을 간결하게 유지한다.
  const changeStatusGroup = useCallback(
    (next: PathPickOrderStatusGroup) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          statusGroup: next === DEFAULT_STATUS_GROUP ? undefined : next,
          pageNumber: undefined,
        })
      );
    },
    [setFilter]
  );

  const applyQuery = useCallback(
    (query?: string) => {
      setFilter((prev) => normalize({ ...prev, query, pageNumber: undefined }));
    },
    [setFilter]
  );

  const changeStartDate = useCallback(
    (startDate?: Date) => {
      setFilter((prev) =>
        normalize({ ...prev, startDate, pageNumber: undefined })
      );
    },
    [setFilter]
  );

  const changeEndDate = useCallback(
    (endDate?: Date) => {
      setFilter((prev) =>
        normalize({ ...prev, endDate, pageNumber: undefined })
      );
    },
    [setFilter]
  );

  // 시작일·종료일을 한 번에 비운다.
  // setFilter의 updater는 렌더 시점 값을 그대로 물고 있어(useTypedSearchParams),
  // changeStartDate → changeEndDate로 이어 부르면 뒤 호출이 앞의 결과를 덮어써
  // 시작일이 그대로 남는다. 두 필드를 한 번의 갱신으로 지워야 한다.
  const clearDateRange = useCallback(() => {
    setFilter((prev) =>
      normalize({
        ...prev,
        startDate: undefined,
        endDate: undefined,
        pageNumber: undefined,
      })
    );
  }, [setFilter]);

  const setPageNumber = useCallback(
    (page: number) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          pageNumber: page === DEFAULT_PAGE_NUMBER ? undefined : page,
        })
      );
    },
    [setFilter]
  );

  const setPageSize = useCallback(
    (size: number) => {
      setFilter((prev) =>
        normalize({
          ...prev,
          pageSize: size === DEFAULT_PAGE_SIZE ? undefined : size,
          pageNumber: undefined,
        })
      );
    },
    [setFilter]
  );

  return {
    filter,
    statusGroup,
    query: filter.query,
    startDate: filter.startDate,
    endDate: filter.endDate,
    pageNumber,
    pageSize,
    changeStatusGroup,
    applyQuery,
    changeStartDate,
    changeEndDate,
    clearDateRange,
    setPageNumber,
    setPageSize,
  };
};

export default usePathPickOrderHistoryFilter;
